import React from 'react';
import { gql, useApolloClient } from '@apollo/client';
import type { RegisterOptions } from 'react-hook-form';

import {
  CoreSnippetFieldsFragment,
  CreateSnippetMutationOptions,
  GetSnippetsQuery,
  GetSnippetsQueryVariables,
  GetSnippetsDocument
} from 'models/graphql';
import { useCreateSnippetMutation, useUpdateSnippetMutation } from 'models/graphql';
import { useSelectedOrganization } from 'hooks/useSelectedOrganization';
import { CreateEditSnippetModalController } from './CreateEditSnippetModal.controller';

export type CreateEditSnippetModalApolloProps = {
  mode: 'create' | 'edit';
  onClose: () => unknown;
  defaultName?: string;
  defaultContent?: string;
  nameRules?: RegisterOptions;
  contentRules?: RegisterOptions;
  maxHashtagCount?: number;
  maxContentLength?: number;
  snippetToEdit?: CoreSnippetFieldsFragment | null;
};

gql`
  mutation CreateSnippet($workspaceId: ID!, $input: CreateSnippetInput!) {
    createSnippet(workspaceId: $workspaceId, input: $input) {
      ...CoreSnippetFields
    }
  }

  mutation UpdateSnippet($snippetId: ID!, $input: UpdateSnippetInput!) {
    updateSnippet(snippetId: $snippetId, input: $input) {
      ...CoreSnippetFields
    }
  }
`;

export const CreateEditSnippetModalApollo = (props: CreateEditSnippetModalApolloProps): JSX.Element => {
  const { cache } = useApolloClient();
  const { selectedWorkspace } = useSelectedOrganization();

  const [createSnippetMutation, createSnippetMutationData] = useCreateSnippetMutation();
  const [updateSnippetMutation, updateSnippetMutationData] = useUpdateSnippetMutation();

  const createSnippetMutationHandler = (options?: CreateSnippetMutationOptions) =>
    createSnippetMutation(options).then(response => {
      const data = cache.readQuery<GetSnippetsQuery, GetSnippetsQueryVariables>({
        // this then block is doing only 1 thing- push newly created snippet into apollo cache
        // to avoid refetch
        // and return response to allow lower levels to chain
        query: GetSnippetsDocument,
        variables: {
          workspaceId: selectedWorkspace?.id as string
        }
      });

      if (!response?.data?.createSnippet || !data?.workspace) {
        return response;
      }

      const updatedWorkspaceSnippets: GetSnippetsQuery = {
        workspace: {
          __typename: 'Workspace',
          id: data.workspace.id,
          snippets: [...data.workspace.snippets, response.data.createSnippet]
        }
      };

      cache.writeQuery<GetSnippetsQuery, GetSnippetsQueryVariables>({
        query: GetSnippetsDocument,
        variables: {
          workspaceId: selectedWorkspace?.id as string
        },
        data: updatedWorkspaceSnippets
      });

      return response;
    });

  return (
    <CreateEditSnippetModalController
      {...props}
      loading={createSnippetMutationData.loading || updateSnippetMutationData.loading}
      updateSnippetMutation={updateSnippetMutation}
      createSnippetMutation={createSnippetMutationHandler}
    />
  );
};
